import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useFilters } from '@/context/filters-context';
import { cn } from '@/lib/utils';
import { ChevronDown, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AttributesFilter } from './attributes';
import { AvailabilityFilter } from './availability';
import { LocationFilter } from './location';
import { PriceFilter } from './price';
import { ServicesFilter } from './services';

// ---------------------------------------------------------------------------
// Shared inner content — used by both sidebar and sheet
// ---------------------------------------------------------------------------

const FiltersContent = ({ inSheet = false }: { inSheet?: boolean }) => {
  const { t } = useTranslation();
  const { filters, setFilters, resetFilters } = useFilters();

  return (
    <>
      <div className={cn('mb-4 flex items-center justify-between', inSheet && 'pr-8')}>
        <h2 className="text-lg font-bold">
          {inSheet ? t('filters.title_search') : t('filters.title')}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={resetFilters}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="mr-1 h-4 w-4" />
          {t('actions.clear_all')}
        </Button>
      </div>

      {/* Search input — mobile sheet only */}
      {inSheet && (
        <div className="mb-4 flex h-9 items-center gap-2 rounded-md border border-input bg-transparent px-3 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('search.placeholder')}
            value={filters.q}
            onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
            className="flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground md:text-sm"
            autoComplete="off"
          />
        </div>
      )}

      {/* 1. Location — geographic context, always first */}
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="group flex w-full items-center justify-between py-2">
          <span className="font-medium">{t('filters.location')}</span>
          <ChevronDown className="h-5 w-5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <LocationFilter />
        </CollapsibleContent>
      </Collapsible>

      {/* 2. Availability — high-intent "when" filter */}
      <Collapsible defaultOpen className="mt-4">
        <CollapsibleTrigger className="group flex w-full items-center justify-between py-2">
          <span className="font-medium">{t('filters.availability')}</span>
          <ChevronDown className="h-5 w-5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <AvailabilityFilter />
        </CollapsibleContent>
      </Collapsible>

      {/* 3. Price — practical budget filter */}
      <Collapsible defaultOpen className="mt-4">
        <CollapsibleTrigger className="group flex w-full items-center justify-between py-2">
          <span className="font-medium">{t('filters.price')}</span>
          <ChevronDown className="h-5 w-5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <PriceFilter />
        </CollapsibleContent>
      </Collapsible>

      {/* 4. Attributes — physical characteristics, personal preference */}
      <Collapsible defaultOpen className="mt-4">
        <CollapsibleTrigger className="group flex w-full items-center justify-between py-2">
          <span className="font-medium">{t('filters.attributes')}</span>
          <ChevronDown className="h-5 w-5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <AttributesFilter />
        </CollapsibleContent>
      </Collapsible>

      {/* 5. Services — explicit content, advanced filter, collapsed by default */}
      <Collapsible className="mt-4">
        <CollapsibleTrigger className="group flex w-full items-center justify-between py-2">
          <span className="font-medium">{t('filters.services')}</span>
          <ChevronDown className="h-5 w-5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <ServicesFilter />
        </CollapsibleContent>
      </Collapsible>
    </>
  );
};

// ---------------------------------------------------------------------------
// Desktop sidebar — hidden below md
// ---------------------------------------------------------------------------

export const FiltersSidebar = () => (
  <div
    data-testid="filters-sidebar"
    className="sticky top-4 hidden h-fit w-64 shrink-0 rounded-lg border border-border bg-card p-4 md:block"
  >
    <FiltersContent />
  </div>
);

// ---------------------------------------------------------------------------
// Mobile sheet — rendered as a slide-up drawer
// ---------------------------------------------------------------------------

export const FiltersSheet = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => (
  <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
    <SheetContent
      side="bottom"
      className="h-[85dvh] overflow-y-auto p-4"
      aria-describedby={undefined}
    >
      <SheetTitle className="sr-only">Filters</SheetTitle>
      <FiltersContent inSheet />
    </SheetContent>
  </Sheet>
);
